/* ============================================================================
   Tradução dos valores técnicos que aparecem no histórico.

   O PROBLEMA. A trilha de auditoria mostra o valor exatamente como ele foi
   gravado, e para política isso é o enum da Android Management API:

       Copia/Cola Texto   —  →  CROSS_PROFILE_COPY_PASTE_ALLOWED
       Aplicativos(...)   FORCE_INSTALLED  →  BLOCKED

   Quem faz a conferência ("o que mudou naquele dia?") não é quem escreveu a
   integração. `CROSS_PROFILE_COPY_PASTE_ALLOWED` não diz nada a essa pessoa, e
   pior: `WHITELIST` e `KIOSK` significam no sistema o OPOSTO do que a palavra
   sugere em português — na tela de política eles aparecem como "BLOQUEADO".
   Uma auditoria que exige tradução mental é uma auditoria que vai ser lida
   errado.

   O QUE ISTO NÃO FAZ: não muda o que se grava. O valor no banco continua sendo
   o enum — é ele que vai para o Google, é ele que serve de prova, e trocá-lo
   por texto em português quebraria a comparação com a política real. Aqui é só
   a camada de LEITURA: o rótulo aparece para a pessoa e o valor cru fica no
   `title`, a um passar de mouse de distância, para quem precisa dele.

   DE ONDE VÊM OS RÓTULOS: das mesmas listas que a tela de política usa
   (`OPCOES` em ModalPolitica) — é o vocabulário que a pessoa já viu quando
   escolheu a opção. Traduzir aqui com outras palavras seria inventar um
   segundo dicionário. O que não está lá saiu da referência da API:
   https://developers.google.com/android/management/reference/rest/v1/enterprises.policies

   POR QUE UM MAPA ÚNICO, e não um por campo: o histórico entrega `campo` como
   texto livre montado no backend ("Aplicativos(Camera Motorola)"), então não há
   chave estável para escolher a tabela certa. Os enums da API são globalmente
   distintos — `BLOCKED` só existe em installType, `LOCATION_ENFORCED` só em
   locationMode —, então um mapa por VALOR não tem colisão. As duas exceções
   ambíguas estão tratadas no fim do arquivo.
   ============================================================================ */

const TRADUCAO: Record<string, string> = {
  // ------------------------------------------------ installType (aplicativos)
  FORCE_INSTALLED: 'Forçar instalação',
  BLOCKED: 'Bloqueado',
  AVAILABLE: 'Disponível para download',
  REQUIRED_FOR_SETUP: 'Obrigatório na configuração',
  KIOSK: 'Modo quiosque',
  PREINSTALLED: 'Pré-instalado',
  INSTALL_TYPE_UNSPECIFIED: 'Não definido',

  // ------------------------------------------------------------ playStoreMode
  WHITELIST: 'Somente os aplicativos da lista',
  BLACKLIST: 'Toda a Play Store liberada',

  // ------------------------------------------------------- modo avião e local
  AIRPLANE_MODE_DISABLED: 'Desativado',
  AIRPLANE_MODE_USER_CHOICE: 'Escolha do usuário',
  LOCATION_DISABLED: 'Desativado',
  LOCATION_ENFORCED: 'Ativado',
  LOCATION_USER_CHOICE: 'Escolha do usuário',

  // -------------------------------------------------------------------- câmera
  CAMERA_ACCESS_DISABLED: 'Desativado',
  CAMERA_ACCESS_ENFORCED: 'Ativado',
  CAMERA_ACCESS_USER_CHOICE: 'Escolha do usuário',

  // ------------------------------------------------------ apps de fora da loja
  DISALLOW_INSTALL: 'Desativado',
  ALLOW_INSTALL_DEVICE_WIDE: 'Ativado',

  // ---------------------------------------------------------------- tethering
  DISALLOW_ALL_TETHERING: 'Bloqueado todos',
  DISALLOW_WIFI_TETHERING: 'Bloqueado Wi-Fi roteador',
  ALLOW_ALL_TETHERING: 'Permitir todos',

  // ------------------------------------------------------ atualização do sistema
  SYSTEM_UPDATE_TYPE_UNSPECIFIED: 'Escolha do usuário',
  WINDOWED: 'Padrão',
  AUTOMATIC: 'Automática',
  POSTPONE: 'Atualização 30 dias depois',

  // ------------------------------------------------------- opções de desenvolvedor
  DEVELOPER_SETTINGS_DISABLED: 'Desativado',
  DEVELOPER_SETTINGS_ALLOWED: 'Ativado',

  // --------------------------------------------------------------------- USB
  DISALLOW_USB_DATA_TRANSFER: 'Todos bloqueados',
  DISALLOW_USB_FILE_TRANSFER: 'Transferência de dados inativa, mouse/teclado ativo',
  ALLOW_USB_DATA_TRANSFER: 'Todos liberados',

  // ------------------------------------------------------------ perfil cruzado
  CROSS_PROFILE_COPY_PASTE_UNSPECIFIED: 'Não especificado',
  COPY_FROM_WORK_TO_PERSONAL_DISALLOWED: 'Trabalho → pessoal bloqueado',
  CROSS_PROFILE_COPY_PASTE_ALLOWED: 'Permitido entre perfis (padrão)',
  CROSS_PROFILE_DATA_SHARING_UNSPECIFIED: 'Não especificado',
  CROSS_PROFILE_DATA_SHARING_DISALLOWED: 'Bloqueado (ambas as direções)',
  DATA_SHARING_FROM_WORK_TO_PERSONAL_DISALLOWED: 'Trabalho → pessoal bloqueado',
  CROSS_PROFILE_DATA_SHARING_ALLOWED: 'Permitido entre perfis (padrão)',

  // -------------------------------------------------------- data e hora, permissão
  AUTO_DATE_AND_TIME_ZONE_USER_CHOICE: 'Escolha do usuário',
  AUTO_DATE_AND_TIME_ZONE_ENFORCED: 'Automático',
  PERMISSION_POLICY_UNSPECIFIED: 'Não definido',
  GRANT: 'Conceder automaticamente',
  DENY: 'Negar automaticamente',
  PROMPT: 'Perguntar ao usuário',

  // --------------------------------------------- personalizacao do quiosque
  /* Os quatro campos de `kioskCustomization`. Eles nao tem seletor proprio na
     tela (saem de "Barra de status", "Botao power" e "Acesso Configuracoes"),
     mas CHEGAM ao historico com o nome do enum. */
  POWER_BUTTON_BLOCKED: 'Botão power bloqueado',
  POWER_BUTTON_AVAILABLE: 'Botão power liberado',
  SETTINGS_ACCESS_BLOCKED: 'Configurações bloqueadas',
  SETTINGS_ACCESS_ALLOWED: 'Configurações liberadas',
  NAVIGATION_DISABLED: 'Navegação desativada',
  NAVIGATION_ENABLED: 'Navegação ativada',
  HOME_BUTTON_ONLY: 'Somente o botão home',
  SYSTEM_INFO_ONLY: 'Só informações do sistema',
  NOTIFICATIONS_AND_SYSTEM_INFO_ENABLED: 'Notificações e informações do sistema',

  // ------------------------------------------- atualizacao automatica do app
  /* Textos conferidos na referencia da API (policy.md, secao autoUpdateMode). */
  AUTO_UPDATE_DEFAULT: 'Padrão (janela de manutenção)',
  AUTO_UPDATE_POSTPONED: 'Adiada em até 90 dias',
  AUTO_UPDATE_HIGH_PRIORITY: 'Assim que possível',

  // ---------------------------------------------------------- uso pessoal
  PERSONAL_USAGE_DISALLOWED: 'Uso pessoal bloqueado',
  PERSONAL_USAGE_ALLOWED: 'Uso pessoal permitido',

  // ---------------------------------------------------------- booleano cru do banco
  /* O backend grava alguns campos como "0"/"1" e outros como "true"/"false".
     Os dois chegam aqui como texto. */
  true: 'Sim',
  false: 'Não',
};

/* AMBIGUIDADE: por que NAO ha tabela por campo.

   'KIOSK' tem dois sentidos na API - em `installType` e "o app roda em
   quiosque"; em `kioskCustomization`/hub seria a interface travada, que a tela
   de politica chama de "BLOQUEADO". Isso pediria desempate por campo.

   So que o diff do historico e montado com RÓTULOS em portugues escritos a mao
   ("Acesso Configurações", "Botão power", "Modo Kiosk"), e nao com o nome do
   campo da API - conferido em ModalPolitica, onde o diff e construido. Nao ha
   chave estavel para indexar uma tabela por campo, e o campo `hub` nem entra no
   diff hoje. Uma tabela indexada por chave que nunca chega e pior que nenhuma:
   parece cobrir o caso e nao cobre.

   Entao 'KIOSK' e traduzido como installType, que e o unico contexto em que ele
   aparece no historico. O parametro `campo` continua na assinatura para o dia
   em que o backend passar a mandar o nome real do campo. */

/**
 * Rótulo legível para um valor do histórico.
 *
 * Devolve o próprio valor quando não há tradução - nome de grupo, texto digitado
 * por alguém, número. Inventar tradução para o que não é enum estragaria mais do
 * que ajudaria.
 */
export function traduzirValor(valor: unknown, _campo?: string): string {
  const bruto = String(valor ?? '').trim();
  if (!bruto) return '';

  return TRADUCAO[bruto] ?? bruto;
}

/** Houve tradução? Serve para decidir se vale mostrar o valor cru no `title`. */
export function foiTraduzido(valor: unknown, campo?: string): boolean {
  const bruto = String(valor ?? '').trim();
  return Boolean(bruto) && traduzirValor(bruto, campo) !== bruto;
}
